import React, { useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Target, Eye, TrendingUp, Focus, ArrowRight } from 'lucide-react';
import { heroImages, whyNotGym, benefits, testimonials, stats } from '../data/mockData';
import { useCountUp } from '../hooks/useCountUp';
import GlassSection from '../components/GlassSection';
import TestimonialsCarousel from '../components/TestimonialsCarousel';

// Stats Component with Animation
const StatsSection = ({ stats }) => {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8 mt-16 pt-16 border-t border-white/10">
      {stats.map((stat) => (
        <StatItem key={stat.id || stat.label} stat={stat} />
      ))}
    </div>
  );
};

const StatItem = ({ stat }) => {
  const numericValue = parseInt(stat.number.replace(/\D/g, ''));
  const [count, ref] = useCountUp(numericValue, 2000);
  const suffix = stat.number.includes('+') ? '+' : stat.number.includes('%') ? '%' : '';

  return (
    <div ref={ref} className="text-center">
      <div className="text-4xl md:text-5xl lg:text-6xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400 mb-2">
        {count}{suffix}
      </div>
      <div className="text-sm md:text-base text-gray-400 font-semibold uppercase tracking-wide break-words px-2">
        {stat.label}
      </div>
    </div>
  );
};

const Home = () => {
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const iconMap = {
    Target,
    Eye,
    TrendingUp,
    Focus
  };

  return (
    <div className="min-h-screen bg-black">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background Image with Overlay - Responsive: vertical for mobile, landscape for desktop */}
        <div className="absolute inset-0">
          <picture>
            <source
              media="(min-width: 768px)"
              srcSet="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1920&q=80&auto=format&fit=crop"
            />
            <img
              src="https://images.pexels.com/photos/2261477/pexels-photo-2261477.jpeg?auto=compress&cs=tinysrgb&w=900&h=1600&fit=crop"
              alt="Intense Training"
              className="w-full h-full object-cover object-center"
            />
          </picture>
          <div className="absolute inset-0 bg-gradient-to-r from-black/85 via-black/65 to-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent"></div>
        </div>

        {/* Content */}
        <div className="relative container mx-auto px-4 py-32">
          <div className="max-w-4xl">
            <div className="inline-block mb-6">
              <span className="px-4 py-2 bg-gradient-to-r from-orange-500/20 to-green-400/20 border border-orange-500/30 rounded-full text-orange-500 font-bold text-sm uppercase tracking-widest">
                Non Siamo Una Palestra
              </span>
            </div>
            
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl xl:text-8xl font-black text-white leading-tight mb-6">
              Allena il <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">Movimento</span>. Costruisci la <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">Performance</span>.
            </h1>
            <p className="text-base sm:text-lg md:text-xl lg:text-2xl text-gray-300 leading-relaxed mb-10 max-w-3xl">
              Se vuoi risultati reali, serve metodo. Noi ti guidiamo. Ogni singolo passo.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 items-stretch sm:items-start w-full">
              <Link to="/contatti" className="w-full sm:w-auto">
                <Button className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-6 sm:px-8 py-6 sm:py-7 text-sm sm:text-base uppercase tracking-wide shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 group whitespace-normal">
                  Prenota la tua valutazione iniziale
                  <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform flex-shrink-0" />
                </Button>
              </Link>
              <Link to="/servizi" className="w-full sm:w-auto">
                <Button variant="outline" className="w-full sm:w-auto border-2 border-white/30 bg-white/10 text-white hover:bg-white hover:text-black font-bold px-6 sm:px-8 py-6 sm:py-7 text-sm sm:text-base uppercase tracking-wide backdrop-blur-sm transition-all duration-300">
                  Inizia ora
                </Button>
              </Link>
            </div>

            {/* Stats with Animation */}
            <StatsSection stats={stats} />
          </div>
        </div>
      </section>

      {/* Benefits Section - Cosa Ottieni */}
      <section className="py-24 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-black text-white mb-6">
                Cosa Ottieni Con <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">Performove</span>
              </h2>
              <p className="text-lg text-gray-400 mb-8">
                Non promesse vuote. Metodo scientifico, professionalità, risultati misurabili.
              </p>
              <ul className="space-y-4 mb-8">
                {benefits.map((benefit) => (
                  <li key={benefit.id} className="flex items-start space-x-3">
                    <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-green-400 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <svg className="w-4 h-4 text-black" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-gray-300 font-medium">{benefit.text}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contatti" className="inline-block w-full sm:w-auto">
                <Button className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-6 sm:px-8 py-5 sm:py-6 text-sm sm:text-base uppercase tracking-wide">
                  Inizia Oggi
                </Button>
              </Link>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-green-400 blur-3xl opacity-20"></div>
              <img
                src="https://images.unsplash.com/photo-1581009146145-b5ef050c2e1e"
                alt="Training"
                className="relative rounded-2xl shadow-2xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-24 bg-gradient-to-b from-zinc-950 to-black">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
              Risultati <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">Reali</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Lasciamo parlare i nostri clienti. Trasformazioni vere, persone vere.
            </p>
          </div>

          <TestimonialsCarousel testimonials={testimonials} />
        </div>
      </section>

      {/* La Differenza Performove - Why Not Gym Section */}
      <section className="py-24 bg-gradient-to-b from-black to-zinc-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
              La Differenza <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">Performove</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Non siamo una palestra tradizionale. Siamo un centro altamente specializzato di personal training.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {whyNotGym.map((item) => {
              const Icon = iconMap[item.icon];
              return (
                <Card key={item.id} className="bg-white/5 border border-white/10 hover:border-orange-500/50 transition-all duration-300 hover:scale-105 group backdrop-blur-sm">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-gradient-to-r from-orange-500 to-green-400 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                      <Icon className="w-7 h-7 text-black" />
                    </div>
                    <h3 className="text-xl font-bold text-white mb-3">
                      {item.title}
                    </h3>
                    <p className="text-gray-400 leading-relaxed">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* I Numeri Non Mentono - Stats Section */}
      <section className="py-24 bg-zinc-950">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
              I Numeri <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">Non Mentono</span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto">
              Dati reali, risultati verificabili, trasformazioni documentate.
            </p>
          </div>

          <StatsSection stats={stats} />
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-black relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-orange-500/10 via-green-400/10 to-orange-500/10"></div>
        <div className="container mx-auto px-4 relative">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl md:text-6xl font-black text-white mb-6">
              Smetti di Rimandare. <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-green-400">Inizia Oggi.</span>
            </h2>
            <p className="text-xl text-gray-300 mb-10">
              La tua prima sessione è completamente gratuita. Zero impegno. Solo risultati.
            </p>
            <Link to="/contatti" className="inline-block w-full sm:w-auto">
              <Button className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold px-6 sm:px-10 py-6 sm:py-8 text-base sm:text-lg uppercase tracking-wide shadow-lg shadow-orange-500/30 hover:shadow-orange-500/50 transition-all duration-300 hover:scale-105 whitespace-normal">
                Prenota la Tua Prova Gratuita
              </Button>
            </Link>
            <p className="text-sm text-gray-500 mt-6">
              Posti limitati disponibili ogni mese
            </p>
          </div>
        </div>
      </section>

      {/* Glass Section - Moved here between CTAs */}
      <GlassSection />
    </div>
  );
};

export default Home;
